import { ActionSettings, Table, useWindowDimensions, LG } from '@fl/cmsch-fe-library';
import { orderBy } from 'lodash/fp';
import React, { FC, memo, useMemo } from 'react';
import { CorrectionReportDetail } from 'api/gen/CorrectionReportDetail';
import { useOurTranslation } from 'app/translations';
import { getColumns } from './columns';
import tableStyles from './styles.sass';

/** Props for the locally created APC reports table. */
interface Props {
  /** Reports created during the current editing session. */
  createdApcReports: Array<CorrectionReportDetail>;
  /** Whether a delete request is currently pending. */
  deleteCreatedReportInProgress?: boolean;
  /** Loads a created row back into the form for editing. */
  editCreatedApcReport(row: CorrectionReportDetail): void;
  /** Deletes a row from the local created-reports table and backend. */
  deleteCreatedApcReport(id: number, isCreatedCorrectionReport: boolean): void;
  /** Clears the locally accumulated created reports. */
  resetCreatedApcReports(): void;
}
const CreatedApcTableBase: FC<Props> = props => {
  const {
    createdApcReports,
    deleteCreatedReportInProgress,
    editCreatedApcReport,
    deleteCreatedApcReport: deleteCreatedCorrectionReport,
    resetCreatedApcReports: resetCreatedMilkabilityReports
  } = props;
  const {
    t
  } = useOurTranslation('analysisProtocolCorrection');
  const {
    width
  } = useWindowDimensions();
  const useVerticalNames = width < LG;
  const actionSettings = useMemo((): ActionSettings<CorrectionReportDetail> => ({
    extraActions: [{
      id: 'delete',
      role: 'delete',
      callback: id => deleteCreatedCorrectionReport(Number(id), true),
      disabledTitle: t('deleteDisabledTitle')
    }, {
      id: 'edit',
      role: 'edit',
      callback: (_, row) => editCreatedApcReport(row),
      confirmClick: true,
      confirmTitle: t('rewriteApcReportFormData'),
      disabledTitle: t('editDisabledTitle'),
      confirmPlacement: 'topLeft'
    }]
  }), [t, deleteCreatedCorrectionReport, editCreatedApcReport]);
  const sortedApcReports = useMemo(() => orderBy(x => x.id, 'desc', createdApcReports), [createdApcReports]);
  const columns = useMemo(() => getColumns(useVerticalNames, t), [useVerticalNames, t]);
  return <div className={tableStyles.reportsTable} data-sentry-component="CreatedApcTableBase" data-sentry-source-file="index.tsx">
            <Table tableId="created-apc-reports-table" columns={columns} rows={sortedApcReports} actionSettings={actionSettings} scroll={{
      x: true
    }} emptyTableMessages={{
      noRows: t('noCorrectionReports')
    }} loading={deleteCreatedReportInProgress} onUnmount={resetCreatedMilkabilityReports} size="small" data-sentry-element="Table" data-sentry-source-file="index.tsx" />
        </div>;
};

/**
 * Shows reports created in the current APC editing session.
 *
 * @example
 * <CreatedApcReportsTable
 *   createdApcReports={reports}
 *   editCreatedApcReport={handleEdit}
 *   deleteCreatedApcReport={handleDelete}
 *   resetCreatedApcReports={handleReset}
 * />
 */
export const CreatedApcReportsTable = memo(CreatedApcTableBase);